const $$splitComponentImporter = () => import('/home/runner/work/zngly-corporate/zngly-corporate/wp-content/themes/zngly-corporate/frontend/src/routes/dash/znglists/reorder.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { SortableList } from '@/Components/dnd/SortableList';
import GridCard from '@/Components/multiViewPicker/views/grid/GridCard';
import { getCatalogMediaFeaturedImage, prettyVisibility } from '@/Components/posts/utils/catalog';
import { requireUserPostCaps } from '@/Router';
import { ImageZnglistPlaceHolder } from '@/assets/images';
import { updateZnglistOrderMutation } from '@/graphql/mutations/Znglist.mutation';
import { ZnglistsQuery, type Znglists_Pagination_Fragment$data, type Znglists_Pagination_Fragment$key, type Znglists_Query } from '@/graphql/queries/Znglist.query';
import { RelayEnvironment } from '@/graphql/relay-env';
import { usePaginationFragment } from '@/graphql/relay.utils';
import type { ZnglyVideo_Znglycontent } from '@/graphql/types';
import { MediaItemSizeEnum } from '@/graphql/types';
import type { DragEndEvent } from '@dnd-kit/core';
import { AccountBox, Tune } from '@mui/icons-material';
import BuildIcon from '@mui/icons-material/Build';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, Container, Stack } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { loadQuery } from 'react-relay';
import { toast } from 'react-toastify';
type ReorderComponentProps = {
  isFallback?: boolean;
} & Partial<RelayData>;
type RelayData = ReturnType<typeof usePaginationFragment<Znglists_Query, Znglists_Pagination_Fragment$key>>;
const prettyStr = (str: string | null = '') => {
  if (!str || str?.length < 1) return '';

  // uppercase the first letter
  const firstLetter = str[0].toUpperCase();
  const restOfStr = str.slice(1);
  return `${firstLetter}${restOfStr}`;
};
const getImageFromZnglistMetadata = (znglist: NonNullable<Znglists_Pagination_Fragment$data['znglists']>['edges'][number]['node']) => {
  if (!znglist?.znglist?.znglyContents) return ImageZnglistPlaceHolder;
  const znglyVideoContent = znglist?.znglist?.znglyContents?.[0]?.znglyContent;
  if (znglyVideoContent) {
    return getCatalogMediaFeaturedImage(znglyVideoContent as ZnglyVideo_Znglycontent, MediaItemSizeEnum.Large) ?? ImageZnglistPlaceHolder;
  }
  return ImageZnglistPlaceHolder;
};
const gridProps = (znglist: ReturnType<typeof toZnglistState>[number]) => {
  let imgUrl = ImageZnglistPlaceHolder;
  if (!!znglist?.node?.znglist?.znglyContents) {
    const znglyVideoContent = znglist?.node?.znglist?.znglyContents?.[0]?.znglyContent;
    if (znglyVideoContent) {
      imgUrl = getCatalogMediaFeaturedImage(znglyVideoContent as ZnglyVideo_Znglycontent, MediaItemSizeEnum.Large) ?? ImageZnglistPlaceHolder;
    }
  }
  return {
    id: znglist.id,
    header: znglist?.title ?? '',
    subheader: '‎',
    imageUrl: imgUrl,
    useHoverButtons: false,
    gridDetails: [{
      icon: <AccountBox />,
      label: znglist.node?.author?.node?.name,
      tooltip: `Author: ${znglist.node?.author?.node?.name}`
    }, {
      icon: <VisibilityIcon />,
      label: (() => {
        return prettyVisibility(znglist?.node?.status);
      })(),
      tooltip: `Visibility: ${prettyVisibility(znglist?.node?.status)}`
    }, {
      icon: <CalendarMonthIcon />,
      label: znglist.date ? znglist.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      tooltip: `Creation date: ${znglist.date?.slice(0, 10)}`
    }, {
      icon: <EditCalendarIcon />,
      label: znglist.modified ? znglist.modified.slice(0, 10) : new Date().toISOString().slice(0, 10),
      tooltip: `Modified date: ${znglist.modified?.slice(0, 10)}`
    }, {
      icon: <Tune />,
      label: prettyStr(znglist.templateType),
      tooltip: `Template: ${prettyStr(znglist.templateType)}`
    }]
  };
};
const toZnglistState = (znglist: Znglists_Pagination_Fragment$data['znglists']) => {
  if (!znglist?.edges?.length) return [];
  return znglist.edges.map(({
    node
  }) => ({
    id: node.id,
    title: node.title,
    status: node.status,
    link: node.link,
    slug: node.slug,
    date: node.date,
    modified: node.modified,
    trashDate: node.trashDate,
    trashTimeUntil: node.trashTimeUntil,
    node: node,
    imageUrl: getImageFromZnglistMetadata(node),
    templateType: node?.znglist?.templateType
  }));
};
const ReorderComponent = (props: ReorderComponentProps) => {
  const navigateBack = useNavigate();
  const [znglists, setZnglists] = useState(toZnglistState(props.data?.znglists));
  const [isSaving, setIsSaving] = useState(false);
  const [newZnglists, setNewZnglists] = useState(znglists);
  useEffect(() => {
    const newZnglists = toZnglistState(props.data?.znglists);
    setZnglists(newZnglists);
    setNewZnglists(newZnglists);
  }, [props.data]);
  const onDragEnd = ({
    active,
    over
  }: DragEndEvent) => {
    const oldIndex = active?.data?.current?.sortable?.index;
    const newIndex = over?.data?.current?.sortable?.index;
    if (oldIndex === newIndex) return;
    const updatedZnglists = [...newZnglists]; // Create a copy of the hubs array
    const [removed] = updatedZnglists.splice(oldIndex, 1); // Remove the dragged hub from its original position
    updatedZnglists.splice(newIndex, 0, removed); // Insert the dragged hub at its new position
    setNewZnglists(updatedZnglists);
  };
  const onSave = () => {
    const toUpdate = [] as {
      id: string;
      order: number;
    }[];
    newZnglists.forEach((znglist, index) => {
      const newOrder = index + 1;
      const currentOrder = znglist?.node?.znglist?.order;
      if (newOrder !== currentOrder) {
        toUpdate.push({
          id: znglist.id,
          order: newOrder
        });
      }
    });
    if (toUpdate.length < 1) {
      navigateBack({
        to: '/dash/znglists'
      });
    }
    setIsSaving(true);
    let updated = 0;
    for (const znglist of toUpdate) {
      updateZnglistOrderMutation({
        input: {
          id: znglist.id,
          order: znglist.order // use the updated order
        }
      }, {
        onCompleted: (data, errors) => {
          updated++;
          if (!!errors?.length) {
            setIsSaving(false);
            toast.error('Failed to update hubs order. Refresh the page and try again.');
          }
          if (updated === toUpdate.length) {
            setIsSaving(false);
            navigateBack({
              to: '/dash/znglists'
            });
          }
        }
      });
    }
  };
  return <div>
      <Container maxWidth="lg">
        <Stack className="FilterBar sticky" direction="row" justifyContent="center">
          <Button className="z-btn-6" variant="outlined" style={{
          width: '100%'
        }} startIcon={<BuildIcon />} onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Container>
      <div style={{
      padding: '0.5em'
    }} />
      <div>
        <SortableList items={newZnglists} onDragEnd={onDragEnd} grid disabled={props?.isFallback || props?.isRefetching}>
          {(znglist, isDragging, index) => {
          return <GridCard key={`zngly_grid_list_card_${index.toString()}_${znglist?.id}`} {...gridProps(znglist)} node={znglist.node} isFallback={isDragging} showCheckbox={true} isLast={index === newZnglists.length - 1} imageUrl={znglist.imageUrl} />;
        }}
        </SortableList>
      </div>
    </div>;
};
export const Route = createFileRoute('/dash/znglists/reorder')({
  component: lazyRouteComponent($$splitComponentImporter, 'component', () => Route.ssr),
  pendingComponent: () => <ReorderComponent isFallback />,
  loader: () => {
    return loadQuery<Znglists_Query>(RelayEnvironment, ZnglistsQuery, {}, {
      fetchPolicy: 'store-or-network'
    });
  },
  beforeLoad: () => {
    requireUserPostCaps('znglist', 'publish_posts', {
      to: '/dash/znglists'
    });
  }
});